// v14at-p4 F4 — NotificationCenter: app-wide badge + dropdown met active jobs.
//
// Rendert enkel wanneer async-flow aan staat (frontend-flag) EN er is
// minimaal 1 job in de store. Leest rechtstreeks uit de centrale store
// (O4-default); gebruikt geen extra hook.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { Bell } from "lucide-react";
import { useMeasurementJobs } from "../../state/measurementJobsStore";
import { NotificationItem } from "./NotificationItem";

export function NotificationCenter() {
  const { jobs, asyncEnabled, cancelJob, dismissJob, markReviewed } =
    useMeasurementJobs();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const list = useMemo(() => {
    // v14at-p7 Groep A — `applied` jobs verdwijnen onmiddellijk uit de
    // NotificationCenter (bestaand gedrag; blijft correct). `dismissed` is
    // vandaag enkel in-memory via JOB_REMOVED. p7.1 vervangt dit door een
    // backend-persistente filter (dismissed_at != null).
    const arr = Object.values(jobs || {}).filter((j) => j.ui_state !== "applied");
    arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return arr;
  }, [jobs]);

  if (!asyncEnabled || list.length === 0) return null;

  // v14at-p7 — Trigger-label telt echte lifecycle-categorieën separaat.
  // `reviewed` is user-wacht en wordt NIET als "bezig" geteld — anders zou
  // de spinner-pulse van de badge misleidend zijn.
  const completedCount = list.filter(
    (j) => j.ui_state === "completed" || j.ui_state === "reviewed"
  ).length;
  const pendingCount = list.filter(
    (j) => j.ui_state === "creating" || j.ui_state === "pending" || j.ui_state === "applying"
  ).length;
  const failedCount = list.filter((j) => j.ui_state === "failed").length;
  const hasFlash = list.some((j) => j.flash_pending);

  const triggerLabel =
    completedCount > 0
      ? `✓ ${completedCount} meting${completedCount > 1 ? "en" : ""} klaar`
      : pendingCount > 0
      ? `🔧 ${pendingCount} meting${pendingCount > 1 ? "en" : ""} bezig`
      : failedCount > 0
      ? `⚠ ${failedCount} meting${failedCount > 1 ? "en" : ""} mislukt`
      : `${list.length} meting${list.length > 1 ? "en" : ""}`;

  const handleReview = (job) => {
    markReviewed(job.job_id);
    setOpen(false);
    if (job.project_id) {
      navigate(`/project/${job.project_id}#zone-${job.zone_id}`);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={`relative border-[#6B8E6B]/40 text-[#2C2C2A] text-xs h-8 ${
            hasFlash ? "ring-2 ring-[#6B8E6B]/60 animate-pulse" : ""
          }`}
          data-testid="notification-center-trigger"
        >
          <Bell className="w-3.5 h-3.5 mr-1.5" />
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] p-0 max-h-[70vh] overflow-y-auto"
        data-testid="notification-center-dropdown"
      >
        <div className="px-3 py-2 border-b border-[#E5E5E1] bg-[#FBFBF8]">
          <div className="text-[12px] font-semibold text-[#2C2C2A]">
            AutoMeasure meldingen
          </div>
          <div className="text-[10.5px] text-[#5D7259] mt-0.5">
            Je kunt verderwerken terwijl een meting loopt.
          </div>
        </div>
        {list.map((job) => (
          <NotificationItem
            key={job.job_id}
            job={job}
            onReview={handleReview}
            onDismiss={(j) => dismissJob(j.job_id)}
            onCancel={(j) => cancelJob(j.job_id)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
